/**
 * Who gave a discount, shown to staff looking an order up.
 *
 * WHY IT IS SEPARATE FROM THE BILL
 * --------------------------------
 * The bill is the customer's receipt — it shows the discount as money off, and
 * that is all a customer needs. Whose decision it was is a question the shop
 * asks itself: a discount is the oldest way to quietly take money out of a
 * till, so being able to see who gave each one, after the fact, is a control.
 *
 * So this sits below the bill and never prints. It names the person for every
 * discount: the manager who authorised one over the limit, or — where no
 * authority was needed — the cashier who rang the order up and gave it within
 * their own limit.
 */
import { formatPKRExact } from '@foodrun/shared';

/** What the discount was, worded as the bill words it. */
function whatItWas(discount) {
  if (discount.reason) return discount.reason;
  return discount.type === 'percentage' ? `Discount (${discount.value}%)` : 'Discount';
}

export function DiscountAudit({ discounts, cashier }) {
  const list = discounts ?? [];
  if (list.length === 0) return null;

  return (
    <div className="no-print mt-3 rounded-lg bg-surface-3 px-3.5 py-3">
      <p className="text-sm font-medium text-ink">
        {list.length === 1 ? 'Discount on this bill' : 'Discounts on this bill'}
      </p>
      <ul className="mt-1.5 space-y-1.5">
        {list.map((discount, index) => {
          // The authoriser where one was needed; otherwise the cashier who gave
          // it within their own limit. 'the till' only if even that is unknown.
          const who = discount.authorisedBy || cashier || 'the till';

          return (
            <li key={index}>
              <div className="flex justify-between gap-3 text-sm">
                <span className="text-ink">{whatItWas(discount)}</span>
                <span className="tabular-nums text-ink-2">
                  -{formatPKRExact(discount.amount ?? 0)}
                </span>
              </div>
              <p className="text-xs text-ink-3">
                Given by {who}
                {discount.authorisedBy ? ' — authorised, over the usual limit' : ''}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
